"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Dropdown = void 0;
var React = _interopRequireWildcard(require("react"));
var _react2 = require("@floating-ui/react");
var _hooks = require("../../hooks");
var _space = require("../../styles/variables/_space");
var _classify = require("../../utils/classify");
var _clickAway = require("../../utils/click-away");
var _mergeRefs = require("../../utils/merge-refs");
var _qa = require("../../utils/qa");
var _Input = require("../Input");
var _Menu = require("../Menu");
var _Tooltip = require("../Tooltip");
var _DropdownModule = _interopRequireDefault(require("./Dropdown.module.css"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
const Dropdown = exports.Dropdown = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    size = 'medium',
    classNames,
    placeholder = 'Select...',
    onChange,
    menu,
    onMenuOpen,
    onMenuClose,
    scrollMenuToBottom = false,
    dropdownInputText = '',
    clickAwayRef,
    elevation = 'modal',
    testId,
    ...inputProps
  } = _ref;
  const menuRef = React.useRef(null);
  const {
    x,
    y,
    refs,
    strategy,
    context
  } = (0, _react2.useFloating)({
    open: true,
    strategy: 'absolute',
    placement: 'bottom-start',
    whileElementsMounted: _react2.autoUpdate,
    middleware: [(0, _react2.flip)(), (0, _react2.offset)(parseInt(_space.spaceXXSmall))]
  });
  const dropdownWidth = (0, _hooks.useReferenceElementWidth)(refs.reference?.current);
  const onMenuToggle = isOpen => {
    if (isOpen) {
      onMenuOpen?.();
    } else {
      onMenuClose?.();
    }
    if (scrollMenuToBottom && menuRef.current && isOpen) {
      menuRef.current.scrollTop = isOpen ? menuRef.current.scrollHeight : 0;
    }
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_clickAway.ClickAway, {
    onChange: onMenuToggle,
    clickAwayRef: clickAwayRef,
    children: _ref2 => {
      let {
        isOpen,
        onOpen,
        clickAway,
        boundaryRef,
        triggerRef
      } = _ref2;
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        className: (0, _classify.classify)(_DropdownModule.default.dropdownContainer, classNames?.wrapper),
        "data-testid": (0, _qa.generateTestId)({
          base: testId,
          slot: 'root'
        }),
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Input.Input, {
          ...inputProps,
          onKeyDown: e => {
            if (e.keyCode === 32) {
              e.preventDefault();
              if (isOpen) {
                clickAway();
              } else {
                onOpen();
              }
            }
          },
          boxRef: (0, _mergeRefs.mergeRefs)([refs.setReference, triggerRef]),
          size: size,
          placeholder: placeholder,
          value: dropdownInputText,
          classNames: {
            box: (0, _classify.classify)(_DropdownModule.default.inputBox, classNames?.box),
            iconRight: classNames?.iconRight
          },
          iconRightName: isOpen ? 'angle-up' : 'angle-down',
          readOnly: true,
          onContainerClick: e => {
            e.stopPropagation();
            onOpen();
          },
          ref: ref,
          testId: (0, _qa.generateTestId)({
            base: testId,
            slot: 'input'
          })
        }), isOpen && menu && /*#__PURE__*/(0, _jsxRuntime.jsx)(_react2.FloatingPortal, {
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_react2.FloatingFocusManager, {
            modal: false,
            context: context,
            initialFocus: refs.reference,
            children: /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
              ref: (0, _mergeRefs.mergeRefs)([refs.setFloating, boundaryRef]),
              className: _DropdownModule.default.menuWrapper,
              "data-testid": (0, _qa.generateTestId)({
                base: testId,
                slot: 'menu-wrapper'
              }),
              style: {
                position: strategy,
                top: y ?? _space.spaceNone,
                left: x ?? _space.spaceNone,
                /* NOTE(Sharad): The FloatingPortal renders the menu outside the normal DOM structure,
                so its parent is effectively the <body> element. This means the menu
                would otherwise default to the body's width. To support fluid width,
                we must manually set the dropdown width here; otherwise, it uses a fixed width.
                Also, Only treat menu as non-fluid if isFluid is strictly false, since default is true in menu and undefined means fluid. */
                ...(menu.isFluid !== false && {
                  '--dropdown-width': dropdownWidth
                }),
                '--menu-elevation': (0, _Tooltip.getElevationValue)(elevation)
              },
              children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Menu.Menu, {
                ...menu,
                testId: (0, _qa.generateTestId)({
                  base: testId,
                  slot: 'menu'
                }),
                onSelect: (option, e) => {
                  onChange?.(option, e);
                  if (
                  // option.keepMenuOpenOnOptionSelect - to allow the menu persist its open stat upon option selection in normal variant
                  !option.keepMenuOpenOnOptionSelect && (!menu.optionsVariant || menu.optionsVariant === 'normal')) {
                    clickAway();
                    if (refs.reference?.current) {
                      refs.reference.current?.querySelector('input')?.focus();
                    }
                  }
                },
                size: menu.size || size,
                onTabOut: clickAway,
                ref: menuRef
              })
            })
          })
        })]
      });
    }
  });
});